import React from "react";
import {
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import produce from "immer";
import { initialState } from "./initialState";

const VISUAL_ACUITY_TYPES = [
  { key: "aided", label: "Aided", hasResultsField: true },
  { key: "unAided", label: "Unaided", hasResultsField: true },
  { key: "pinHole", label: "Pin Hole", hasResultsField: false },
  {
    key: "bestCorrectiveVisualAcuity",
    label: "Best Corrected Visual Acuity",
    hasResultsField: false
  }
];

const EYES = [
  { key: "rEye", label: "Right Eye" },
  { key: "lEye", label: "Left Eye" },
  { key: "both", label: "Both Eyes" },
  { key: "results", label: "Result" }
];

const SEGMENTS = ["Distance", "Near"];

export const unAidedHeaders = ["Unaided", "R-Eye", "L-Eye", "Both", "Results"];
export const aidedHeaders = ["Aided", "R-Eye", "L-Eye", "Both", "Results"];
export const pinHoleHeaders = ["Pin Hole", "R-Eye", "L-Eye", "Both"];
export const bestCorrectedHeaders = ["Best corrected visual acuity", "R-Eye", "L-Eye", "Both"];

interface VisualAcuityProps {
  visionData: Record<string, any>;
  onVisionChange?: (updatedVision: any) => void;
  readOnly?: boolean;
}

export default function VisualAcuity({
  visionData,
  onVisionChange,
  readOnly = false
}: VisualAcuityProps): React.JSX.Element | null {
  const handleChange = (vaType: string, segment: string, eyeKey: string, value: string) => {
    if (readOnly || !onVisionChange) return;
    const updated = produce(visionData, (draft: any) => {
      // Ensure the root structure exists if we are passed the 'eye' object
      // Use initialState to preserve all required fields (null states)
      if (!draft || Object.keys(draft).length === 0) {
        Object.assign(draft, JSON.parse(JSON.stringify(initialState)));
      }

      if (!draft.vision) {
        draft.vision = JSON.parse(JSON.stringify(initialState.vision));
      }

      const { vision } = draft;

      if (!vision[vaType]) {
        // This shouldn't normally happen if initialState has it, but for safety:
        const hasResults = vaType === "unAided" || vaType === "aided";
        const initialEyeData: any = { rEye: null, lEye: null, both: null };
        if (hasResults) initialEyeData.results = null;

        vision[vaType] = {
          Distance: { ...initialEyeData },
          Near: { ...initialEyeData }
        };
      }

      const segmentData = vision[vaType][segment];
      if (segmentData) {
        segmentData[eyeKey] = value === "" ? null : value;
      }
    });
    onVisionChange(updated);
  };

  const currentVision = visionData?.vision || {};

  const hasVAData = (vaKey: string) => {
    const vaData = currentVision[vaKey];
    if (!vaData) return false;
    return SEGMENTS.some((seg) => {
      const segData = vaData[seg];
      if (!segData) return false;
      return EYES.some((eye) => Boolean(segData[eye.key]));
    });
  };

  const visibleVATypes = readOnly
    ? VISUAL_ACUITY_TYPES.filter((va) => hasVAData(va.key))
    : VISUAL_ACUITY_TYPES;

  const cellStyle = {
    border: 1,
    borderColor: "divider",
    minWidth: readOnly ? "auto" : "80px",
    padding: readOnly ? "4px 8px" : "8px"
  };

  const headerCellStyle = {
    ...cellStyle,
    fontWeight: 600,
    textAlign: "center" as const,
    backgroundColor: readOnly ? "transparent" : "inherit"
  };

  if (readOnly && visibleVATypes.length === 0) return null;

  return (
    <TableContainer>
      {!readOnly && (
        <Typography
          sx={{
            padding: 1,
            textAlign: "center",
            backgroundColor: "#ededed",
            borderLeft: 1,
            borderRight: 1,
            borderTop: 1,
            borderColor: "divider"
          }}
        >
          Visual Acuity
        </Typography>
      )}

      <Table
        size="small"
        sx={{
          borderCollapse: "collapse",
          border: readOnly ? "none" : 1,
          borderColor: "divider"
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellStyle} rowSpan={2} />
            {SEGMENTS.map((seg) => (
              <TableCell key={seg} colSpan={EYES.length} sx={headerCellStyle}>
                {seg} Vision
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            {SEGMENTS.map((seg) =>
              EYES.map((eye) => (
                <TableCell key={`${seg}-${eye.key}`} sx={headerCellStyle}>
                  {eye.label}
                </TableCell>
              ))
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {visibleVATypes.map((va) => (
            <TableRow key={va.key}>
              <TableCell sx={{ ...headerCellStyle, textAlign: "left" }}>{va.label}</TableCell>
              {SEGMENTS.map((seg) =>
                EYES.map((eye) => {
                  const showInput = eye.key === "results" ? va.hasResultsField : true;
                  const segmentData = currentVision[va.key]?.[seg];
                  const value = segmentData?.[eye.key] ?? "";

                  let cellContent;
                  if (!showInput) {
                    cellContent = (
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        textAlign="center"
                        display="block"
                      >
                        N/A
                      </Typography>
                    );
                  } else if (readOnly) {
                    cellContent = <Typography variant="body2">{value || "-"}</Typography>;
                  } else {
                    cellContent = (
                      <TextField
                        size="small"
                        variant="outlined"
                        fullWidth
                        value={value}
                        onChange={(e) => handleChange(va.key, seg, eye.key, e.target.value)}
                        sx={{
                          "& .MuiInputBase-input": {
                            padding: "4px 8px",
                            fontSize: "0.875rem"
                          }
                        }}
                      />
                    );
                  }

                  return (
                    <TableCell
                      key={`${seg}-${va.key}-${eye.key}`}
                      sx={{ ...cellStyle, textAlign: "center" }}
                    >
                      {cellContent}
                    </TableCell>
                  );
                })
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
